import { css } from "@emotion/react";
import { useState } from "react";
import type { ExactTheme } from "src/lib/emotion/theme";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import {
  FloatingPortal,
  offset,
  safePolygon,
  size,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";

const HEIGHT = 18;

type Props = { message: string; delay?: number; floating?: boolean };

export function InputErrorMessage({
  message,
  delay = 300,
  floating = true,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    placement: "bottom-start",
    middleware: [
      offset({
        mainAxis: -HEIGHT,
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          const maxHeight = availableHeight - 10;
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`,
            maxWidth: `${Math.min(rects.reference.width, 400)}px`,
            minWidth: `${Math.max(200, rects.reference.width)}px`,
          });
        },
      }),
    ],
    onOpenChange: setIsOpen,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      handleClose: safePolygon(),
      delay: {
        open: delay,
        close: delay,
      },
    }),
  ]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
    },
    open: {
      opacity: 1,
    },
    close: {
      opacity: 0,
    },
  });

  const [errorOverflow, setErrorOverflow] = useState(false);

  const checkErrorOverflow = (node: HTMLDivElement | null) => {
    if (node && node.offsetHeight > HEIGHT) {
      setErrorOverflow(true);
    } else {
      setErrorOverflow(false);
    }
  };

  return (
    <div
      css={containerStyle({
        floating,
      })}
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      <div css={truncatedRowStyle}>{message}</div>
      <div ref={checkErrorOverflow} css={hiddenFullRow}>
        {message}
      </div>

      {isMounted && errorOverflow && (
        <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps({
              style: {
                ...floatingStyles,
                ...transitionStyles,
              },
            })}
            css={fullHeightRowStyle}
          >
            {message}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}

const containerStyle =
  ({ floating }: { floating: boolean }) =>
  ({ colorsV2 }: ExactTheme) =>
    css({
      position: floating ? "absolute" : "relative",
      fontSize: "12px",
      lineHeight: HEIGHT + "px",
      fontStyle: "normal",
      fontWeight: 400,
      color: colorsV2.danger.bg,
      top: floating ? "100%" : 0,
      width: "100%",
    });

const truncatedRowStyle = {
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const fullHeightRowStyle = ({
  colorsV2,
  borderRadiusV2,
  shadowV2,
  zIndices,
}: ExactTheme) =>
  css({
    position: "absolute",
    fontSize: "12px",
    lineHeight: HEIGHT + "px",
    color: colorsV2.danger.bg,
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 2,
    backgroundColor: colorsV2.white,
    borderRadius: borderRadiusV2.primary,
    boxShadow: shadowV2.error,
    zIndex: zIndices.tooltips,
    wordBreak: "break-word",
  });

const hiddenFullRow = css({
  position: "absolute",
  fontSize: "12px",
  lineHeight: HEIGHT + "px",
  visibility: "hidden",
  paddingLeft: 4,
  paddingRight: 4,
  wordBreak: "break-word",
});
